/**
 * API functions for the D6 channel resource/cost stats endpoints.
 * All endpoints are owner/channel_admin only — callers should handle 403.
 */

import { apiClient } from './api';
import type {
  TokenConsumptionResponse,
  TemplateCreationUsageResponse,
  EvalActivityResponse,
  CreditPoolResponse,
  StatsPeriod,
  StatsGroupBy,
} from '@/types/creditStats';

/**
 * Fetch token consumption breakdown for a channel.
 * GET /api/channels/{slug}/stats/token-consumption
 */
export async function getTokenConsumption(
  slug: string,
  period: StatsPeriod,
  groupBy: StatsGroupBy,
): Promise<TokenConsumptionResponse> {
  const { data } = await apiClient.get<TokenConsumptionResponse>(
    `/channels/${slug}/stats/token-consumption`,
    { params: { period, group_by: groupBy } },
  );
  return data;
}

/**
 * Fetch per-coach template creation usage for the current month.
 * GET /api/channels/{slug}/stats/creation-usage
 */
export async function getCreationUsage(
  slug: string,
): Promise<TemplateCreationUsageResponse> {
  const { data } = await apiClient.get<TemplateCreationUsageResponse>(
    `/channels/${slug}/stats/creation-usage`,
  );
  return data;
}

/**
 * Fetch weekly eval activity buckets for a channel.
 * GET /api/channels/{slug}/stats/eval-activity
 */
export async function getEvalActivity(
  slug: string,
  period: StatsPeriod,
): Promise<EvalActivityResponse> {
  const { data } = await apiClient.get<EvalActivityResponse>(
    `/channels/${slug}/stats/eval-activity`,
    { params: { period } },
  );
  return data;
}

/**
 * Fetch the credit pool summary for a channel.
 * GET /api/channels/{slug}/stats/credit-pool
 */
export async function getCreditPool(
  slug: string,
): Promise<CreditPoolResponse> {
  const { data } = await apiClient.get<CreditPoolResponse>(
    `/channels/${slug}/stats/credit-pool`,
  );
  return data;
}
